import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'

import type { OrderWithRelations } from '@snootsvet/shared'

import {
  ChargeFailedError,
  useChargeInvoice,
} from '@/frontend/features/orders/hooks/use-charge-invoice'
import { useCreateMemberInvoice } from '@/frontend/features/orders/hooks/use-create-member-invoice'
import { useUpdateOrder } from '@/frontend/features/orders/hooks/use-update-order'

/**
 * Builds a stable fingerprint for a set of orders based on line-item data
 * (ID, price, quantity). Intentionally excludes volatile fields like
 * `updatedAt` or `paymentStatus` so that a failed payment attempt doesn't
 * invalidate the cached invoice.
 */
export function buildOrderFingerprint(
  orders: Pick<OrderWithRelations, 'id' | 'totalPrice' | 'quantity'>[],
  orderIds: string[]
): string {
  return orders
    .filter((o) => orderIds.includes(o.id))
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((o) => `${o.id}:${o.totalPrice}:${o.quantity}`)
    .join(',')
}

/**
 * Splits the selected orders into free (zero-price) and chargeable subsets.
 *
 * Carts routinely mix the two - e.g. a £0 preventative alongside a paid gel.
 * Free orders transition straight to collected and must never be sent to the
 * invoice endpoint, which rejects any batch containing a non-chargeable order.
 */
export function splitCheckoutOrders(
  orders: Pick<OrderWithRelations, 'id' | 'totalPrice'>[],
  checkoutOrderIds: string[]
): { freeOrderIds: string[]; chargeableOrderIds: string[] } {
  const selected = orders.filter((o) => checkoutOrderIds.includes(o.id))
  // Totals are never negative (there are no credit/discount lines), so `=== 0`
  // and `> 0` between them account for every order.
  return {
    freeOrderIds: selected.filter((o) => o.totalPrice === 0).map((o) => o.id),
    chargeableOrderIds: selected.filter((o) => o.totalPrice > 0).map((o) => o.id),
  }
}

/**
 * A Stripe invoice already created for a set of orders. The fingerprint is of the orders
 * as they were when it was created, so a later change to them is detected before charging.
 */
export interface CheckoutInvoice {
  id: string
  amountRemaining: number
  /** Email on the invoice's Stripe customer, which a tap-to-pay payment has to carry. */
  customerEmail: string | null
  fingerprint: string
}

export type CheckoutError =
  | { kind: 'invoice-failed'; message?: string }
  | { kind: 'payment-declined'; invoiceUrl: string; hostedInvoiceUrl: string }
  | { kind: 'charge-failed'; message?: string }
  | null

export interface CheckoutState {
  /**
   * Complete a set of orders. Free (zero-price) orders transition directly to
   * collected; chargeable orders are settled via the Stripe invoice + charge
   * flow. Mixed carts handle both subsets in a single call.
   */
  checkout: (
    checkoutOrderIds: string[],
    memberId: string | null,
    paymentMethodId: string | null
  ) => Promise<void>
  checkoutError: CheckoutError
  isSubmitting: boolean
}

/**
 * Manages order completion — either a direct status transition for zero-total
 * orders, or the two-step invoice creation + charging flow for paid orders.
 *
 * For paid orders, tracks the active Stripe invoice in a ref so retrying with
 * a different card reuses the same invoice. Automatically detects when the
 * underlying orders have changed (dirty) and recreates the invoice, including
 * an invoice seeded by the caller. `onSuccess` receives the amount charged.
 */
export function useHandleCheckout(
  orders: OrderWithRelations[],
  {
    invoice,
    onSuccess,
  }: {
    /** Invoice checkout already created for these orders; charged instead of creating a new one. */
    invoice?: CheckoutInvoice | null
    onSuccess?: (charged: number) => void
  } = {}
): CheckoutState {
  const { mutateAsync: createInvoice } = useCreateMemberInvoice()
  const { mutateAsync: chargeInvoice } = useChargeInvoice()
  const { mutateAsync: updateOrder } = useUpdateOrder()

  /** Tracks the active Stripe invoice and the order snapshot it was created for. */
  const activeInvoice = useRef<CheckoutInvoice | null>(null)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [checkoutError, setCheckoutError] = useState<CheckoutError>(null)

  /**
   * Attempt to charge an invoice. Returns true on success, false on failure
   * (after stamping the appropriate checkout error). Charging is the only
   * irreversible step, so callers run it before any cheap/retryable work.
   */
  const attemptCharge = useCallback(
    async (invoiceId: string, paymentMethodId: string): Promise<boolean> => {
      try {
        await chargeInvoice({ invoiceId, paymentMethodId })
        return true
      } catch (err) {
        if (err instanceof ChargeFailedError) {
          setCheckoutError({
            kind: 'payment-declined',
            invoiceUrl: err.invoiceUrl,
            hostedInvoiceUrl: err.hostedInvoiceUrl,
          })
        } else {
          setCheckoutError({
            kind: 'charge-failed',
            message: err instanceof Error ? err.message : undefined,
          })
        }
        return false
      }
    },
    [chargeInvoice]
  )

  /**
   * Transition free (zero-price) orders straight to collected. Cheap and
   * retryable - `complete` only acts on prepared orders, so re-running it
   * after a partial failure is safe. Surfaces a toast if any fail.
   */
  const completeFreeOrders = useCallback(
    async (freeOrderIds: string[]) => {
      if (freeOrderIds.length === 0) return
      const results = await Promise.allSettled(
        freeOrderIds.map((id) => updateOrder({ id, action: 'complete' }))
      )
      const failed = results.filter((r) => r.status === 'rejected').length
      if (failed > 0) {
        toast.error(
          `${failed} of ${freeOrderIds.length} free order${freeOrderIds.length === 1 ? '' : 's'} couldn't be completed - please try again`
        )
      }
    },
    [updateOrder]
  )

  const checkout = useCallback(
    async (checkoutOrderIds: string[], memberId: string | null, paymentMethodId: string | null) => {
      if (checkoutOrderIds.length === 0) return

      setIsSubmitting(true)
      setCheckoutError(null)

      // Free orders never touch Stripe; only chargeable orders hit the invoice
      // endpoint (which rejects any batch containing a £0 line).
      const { freeOrderIds, chargeableOrderIds } = splitCheckoutOrders(orders, checkoutOrderIds)

      // Free-only cart - no charge to run, just transition the free orders.
      if (chargeableOrderIds.length === 0) {
        await completeFreeOrders(freeOrderIds)
        onSuccess?.(0)
        setIsSubmitting(false)
        return
      }

      // Paid checkout - invoice + charge (caller must provide both for chargeable orders)
      if (!memberId || !paymentMethodId) {
        setIsSubmitting(false)
        throw new Error('memberId and paymentMethodId are required for paid checkout')
      }

      const fingerprint = buildOrderFingerprint(orders, chargeableOrderIds)

      if (!activeInvoice.current && invoice) {
        activeInvoice.current = invoice
      }

      // Create a new invoice if we don't have one or the orders have changed
      if (!activeInvoice.current || activeInvoice.current.fingerprint !== fingerprint) {
        try {
          const result = await createInvoice({
            memberId,
            orderIds: chargeableOrderIds,
          })
          activeInvoice.current = {
            id: result.invoiceId,
            fingerprint,
            amountRemaining: result.amountRemaining,
            customerEmail: result.customerEmail,
          }
        } catch (err) {
          setCheckoutError({
            kind: 'invoice-failed',
            message: err instanceof Error ? err.message : undefined,
          })
          setIsSubmitting(false)
          return
        }
      }

      // Charge first - the irreversible step. On failure the free orders are
      // left untouched, so the whole cart stays intact for a clean retry.
      // Account credit applied at finalization can already cover the invoice, in
      // which case the backend has settled the orders and there is nothing to charge.
      const charged =
        activeInvoice.current!.amountRemaining === 0 ||
        (await attemptCharge(activeInvoice.current!.id, paymentMethodId))
      if (!charged) {
        setIsSubmitting(false)
        return
      }

      // Charge succeeded - capture the amount before clearing the invoice, then
      // transition the free orders. A failure here is retryable: the now-paid
      // orders drop out of the cart, so re-running Complete only retries the
      // free transitions (no double charge).
      const amountCharged = activeInvoice.current!.amountRemaining
      activeInvoice.current = null

      await completeFreeOrders(freeOrderIds)
      onSuccess?.(amountCharged)
      setIsSubmitting(false)
    },
    [orders, invoice, createInvoice, attemptCharge, completeFreeOrders, onSuccess]
  )

  return { checkout, checkoutError, isSubmitting }
}
